// Edit demographics for a Standard Calyvra client.
import { useState } from 'react';
import portalApi from '../lib/api';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs text-slate-500">{label}</Label>{children}</div>
);

export function EditClientDialog({ open, onOpenChange, client, constants, onSaved }) {
  const d = client?.demographics || {};
  const [form, setForm] = useState({
    first_name: d.first_name || '', middle_name: d.middle_name || '', last_name: d.last_name || '',
    date_of_birth: d.date_of_birth || '', sex: d.sex || '', preferred_language: d.preferred_language || '',
    phone: d.phone || '', email: d.email || '',
    street_address: d.street_address || '', apartment: d.apartment || '', city: d.city || '',
    state: d.state || '', zip_code: d.zip_code || '', county: d.county || '',
    ssn: '', immigration_status: d.immigration_status || '', marital_status: d.marital_status || '',
    occupation: d.occupation || '', income_period: d.income_period || '', income_amount: d.income_amount ?? '',
    source: client?.source || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const demographics = {
        first_name: form.first_name || null, middle_name: form.middle_name || null, last_name: form.last_name || null,
        date_of_birth: form.date_of_birth || null, sex: form.sex || null, preferred_language: form.preferred_language || null,
        phone: form.phone || null, email: form.email || null,
        street_address: form.street_address || null, apartment: form.apartment || null, city: form.city || null,
        state: form.state || null, zip_code: form.zip_code || null, county: form.county || null,
        immigration_status: form.immigration_status || null, marital_status: form.marital_status || null,
        occupation: form.occupation || null, income_period: form.income_period || null,
        income_amount: form.income_amount !== '' ? parseFloat(form.income_amount) : null,
      };
      if (form.ssn) demographics.ssn = form.ssn;
      await portalApi.updateClient(client.id, { source: form.source || null, demographics });
      toast.success('Client updated');
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
        <DialogHeader><DialogTitle>Edit Client</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <F label="First Name"><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="edit-client-first-name" /></F>
          <F label="Middle Name"><Input value={form.middle_name} onChange={(e) => set('middle_name', e.target.value)} /></F>
          <F label="Last Name"><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="edit-client-last-name" /></F>
          <F label="Date of Birth"><Input type="date" value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} /></F>
          <F label="Sex">
            <Select value={form.sex || 'none'} onValueChange={(v) => set('sex', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(constants?.client_sexes || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Preferred Language">
            <Select value={form.preferred_language || 'none'} onValueChange={(v) => set('preferred_language', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(constants?.client_languages || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Phone"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} /></F>
          <F label="Email"><Input value={form.email} onChange={(e) => set('email', e.target.value)} /></F>
          <F label="SSN (leave blank to keep)"><Input value={form.ssn} onChange={(e) => set('ssn', e.target.value)} placeholder="•••-••-••••" /></F>
          <F label="Street Address"><Input value={form.street_address} onChange={(e) => set('street_address', e.target.value)} /></F>
          <F label="Apt / Unit"><Input value={form.apartment} onChange={(e) => set('apartment', e.target.value)} /></F>
          <F label="City"><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></F>
          <F label="State">
            <Select value={form.state || 'none'} onValueChange={(v) => set('state', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(constants?.us_states || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="ZIP Code"><Input value={form.zip_code} onChange={(e) => set('zip_code', e.target.value)} /></F>
          <F label="County"><Input value={form.county} onChange={(e) => set('county', e.target.value)} /></F>
          <F label="Marital Status">
            <Select value={form.marital_status || 'none'} onValueChange={(v) => set('marital_status', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(constants?.marital_statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Immigration Status">
            <Select value={form.immigration_status || 'none'} onValueChange={(v) => set('immigration_status', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(constants?.immigration_statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Occupation"><Input value={form.occupation} onChange={(e) => set('occupation', e.target.value)} /></F>
          <F label="Income Period">
            <Select value={form.income_period || 'none'} onValueChange={(v) => set('income_period', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(constants?.income_periods || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Income Amount"><Input type="number" value={form.income_amount} onChange={(e) => set('income_amount', e.target.value)} /></F>
          <F label="Source">
            <Select value={form.source || 'none'} onValueChange={(v) => set('source', v === 'none' ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="none">—</SelectItem>{(constants?.standard_client_sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-client-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditClientDialog;
